// documents.repo — ambil data untuk dokumen (Nota Servis, Tanda Terima).
import { supabase } from '@core/supabase';
import { rowToTicket, fetchRounds, MONEY_COLS, type TicketRow } from '@core/ticketMap';
import { tglHuman } from '@domain/format';
import type { Ticket } from '@core/types';

export interface NotaData {
  ticket: Ticket; no: string; token: string; unit: string; sn: string; tindakan: string; garansi: string;
  tglAmbil: string; jatuhTempo: string;
  custNama: string; custWa: string; outletNama: string; outletAlamat: string; outletWa: string;
}

/** Metadata umum dokumen (Surat Kuasa, Tracking, Label). */
/** Data "Diwakilkan" persisten (Surat Kuasa) — pengambil + foto bukti (path bucket 'kuasa'). */
export interface DiwakilkanData {
  pengambil: { nama: string; ktp: string; alamat: string; hp: string; hubungan: string };
  foto?: string | null; at?: string; by?: string;
  /** Tanda tangan pengambil di layar (PNG data-URI). Pengesahan bila surat tak dicetak. */
  ttd?: string | null;
}

export interface DocMeta {
  no: string; token: string; status: string; hold: boolean; tgl: string; tglAmbil: string; outlet: string;
  custNama: string; custWa: string; unit: string; jenis: string; merk: string; model: string;
  sn: string; kelengkapan: string[]; keluhan: string; kota: string;
  outletNama: string; outletAlamat: string; outletWa: string; penerima: string;
}

export interface TandaData {
  no: string; token: string; tgl: string; custNama: string; custWa: string;
  unit: string; sn: string; warna: string; sandi: string; kelengkapan: string[]; kondisi: string; keluhan: string;
  estBiaya: number; estSelesai: string; dp: number; sisa: number; penerima: string;
  outletNama: string; outletAlamat: string; outletWa: string;
  caseKhusus: boolean; caseKhususKet: string;
}

interface Embed { nama?: string; alamat?: string; wa?: string; kota?: string }
type Raw = TicketRow & {
  token?: string | null; unit?: string | null; sn?: string | null; tindakan?: string | null; garansi?: string | null;
  jatuh_tempo?: string | null; customers: Embed | Embed[] | null; outlets: Embed | Embed[] | null;
};
const one = (e: Embed | Embed[] | null): Embed => (Array.isArray(e) ? (e[0] ?? {}) : (e ?? {}));

export async function getNota(no: string): Promise<NotaData> {
  const { data, error } = await supabase()
    .from('tickets')
    .select(`${MONEY_COLS},token,unit,sn,tindakan,garansi,jatuh_tempo,customers(nama,wa),outlets(nama,alamat,wa)`)
    .eq('no', no).single();
  if (error) throw new Error(error.message);
  const r = data as unknown as Raw;
  const c = one(r.customers), o = one(r.outlets);
  const rounds = (await fetchRounds([r.no])).get(r.no) ?? [];
  return {
    ticket: rowToTicket(r, rounds), no: r.no, token: r.token ?? '', unit: r.unit ?? '—', sn: r.sn ?? '', tindakan: r.tindakan ?? '—',
    garansi: r.garansi ?? 'Tidak Ada',
    tglAmbil: r.tgl_ambil_iso ? tglHuman(new Date(r.tgl_ambil_iso)) : '—',
    jatuhTempo: r.jatuh_tempo ? tglHuman(new Date(r.jatuh_tempo)) : '',
    custNama: c.nama ?? '—', custWa: c.wa ?? '',
    outletNama: o.nama ?? 'iFiCs', outletAlamat: o.alamat ?? '', outletWa: o.wa ?? '',
  };
}

interface TandaRaw {
  no: string; token: string | null; tgl_iso: string | null; est_biaya: number | null; est_selesai: string | null; dp: number | null;
  penerima: string | null; unit: string | null; jenis: string | null; merk: string | null; model: string | null;
  sn: string | null; warna: string | null; sandi: string | null; kelengkapan: unknown; kondisi: string | null; keluhan: string | null;
  case_khusus: boolean | null; case_khusus_ket: string | null;
  customers: Embed | Embed[] | null; outlets: Embed | Embed[] | null;
}

export async function getTandaTerima(no: string): Promise<TandaData> {
  const { data, error } = await supabase()
    .from('tickets')
    .select('no,token,tgl_iso,est_biaya,est_selesai,dp,penerima,unit,jenis,merk,model,sn,warna,sandi,kelengkapan,kondisi,keluhan,case_khusus,case_khusus_ket,customers(nama,wa),outlets(nama,alamat,wa)')
    .eq('no', no).single();
  if (error) throw new Error(error.message);
  const r = data as unknown as TandaRaw;
  const c = one(r.customers), o = one(r.outlets);
  const unit = (r.unit ?? [r.jenis, r.merk, r.model].filter(Boolean).join(' ')) || '—';
  const est = r.est_biaya ?? 0, dp = r.dp ?? 0;
  return {
    no: r.no, token: r.token ?? '', tgl: r.tgl_iso ? tglHuman(new Date(r.tgl_iso)) : '—',
    custNama: c.nama ?? '—', custWa: c.wa ?? '', unit, sn: r.sn ?? '', warna: r.warna ?? '', sandi: r.sandi ?? '',
    kelengkapan: Array.isArray(r.kelengkapan) ? (r.kelengkapan as string[]) : [],
    kondisi: r.kondisi ?? '', keluhan: r.keluhan ?? '—',
    estBiaya: est, estSelesai: r.est_selesai ? tglHuman(new Date(r.est_selesai)) : '', dp, sisa: Math.max(0, est - dp), penerima: r.penerima ?? '—',
    outletNama: o.nama ?? 'iFiCs', outletAlamat: o.alamat ?? '', outletWa: o.wa ?? '',
    caseKhusus: r.case_khusus ?? false, caseKhususKet: r.case_khusus_ket ?? '',
  };
}

interface MetaRaw {
  no: string; token: string | null; status: string; hold: boolean | null; tgl_iso: string | null; tgl_ambil_iso: string | null;
  outlet: string | null; unit: string | null; jenis: string | null; merk: string | null; model: string | null; sn: string | null;
  kelengkapan: unknown; keluhan: string | null; penerima: string | null;
  customers: Embed | Embed[] | null; outlets: Embed | Embed[] | null;
}

export async function getDocMeta(no: string): Promise<DocMeta> {
  const { data, error } = await supabase()
    .from('tickets')
    .select('no,token,status,hold,tgl_iso,tgl_ambil_iso,outlet,unit,jenis,merk,model,sn,kelengkapan,keluhan,penerima,customers(nama,wa),outlets(nama,alamat,wa,kota)')
    .eq('no', no).single();
  if (error) throw new Error(error.message);
  const r = data as unknown as MetaRaw;
  const c = one(r.customers), o = one(r.outlets);
  const unit = (r.unit ?? [r.jenis, r.merk, r.model].filter(Boolean).join(' ')) || '—';
  return {
    no: r.no, token: r.token ?? '', status: r.status, hold: r.hold ?? false, outlet: r.outlet ?? '',
    tgl: r.tgl_iso ? tglHuman(new Date(r.tgl_iso)) : '—', tglAmbil: r.tgl_ambil_iso ? tglHuman(new Date(r.tgl_ambil_iso)) : '',
    custNama: c.nama ?? '—', custWa: c.wa ?? '', unit, jenis: r.jenis ?? '', merk: r.merk ?? '', model: r.model ?? '',
    sn: r.sn ?? '', kelengkapan: Array.isArray(r.kelengkapan) ? (r.kelengkapan as string[]) : [], keluhan: r.keluhan ?? '—',
    kota: o.kota ?? '', outletNama: o.nama ?? 'iFiCs', outletAlamat: o.alamat ?? '', outletWa: o.wa ?? '', penerima: r.penerima ?? '—',
  };
}

/** Ambil data Surat Kuasa tersimpan (prefill/reprint). Graceful: null bila kolom belum ada (pra-0054) atau kosong. */
export async function getDiwakilkan(no: string): Promise<DiwakilkanData | null> {
  const { data, error } = await supabase().from('tickets').select('diwakilkan').eq('no', no).maybeSingle();
  if (error) return null; // kolom belum di-push / RLS → anggap belum ada (dokumen lain tak terpengaruh)
  return ((data as { diwakilkan?: DiwakilkanData | null } | null)?.diwakilkan) ?? null;
}

/** Simpan data Surat Kuasa (pengambil + foto) ke tiket. RLS guard_ticket_* tak menyentuh kolom ini. */
export async function saveDiwakilkan(no: string, d: DiwakilkanData): Promise<void> {
  const { error } = await supabase().from('tickets').update({ diwakilkan: d }).eq('no', no);
  if (error) throw new Error(error.message);
}
